import type { UserStory, ProjectStories, AcceptanceCriteria } from "@/features/Projects";

// Generate unique ID for stories and criteria
export const generateStoryId = (): string => 
  `story-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const generateCriteriaId = (): string => 
  `ac-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Create a new default story
export const createNewStory = (): UserStory => ({
  id: generateStoryId(),
  title: "Nova User Story",
  description: "Como usuário, eu quero uma nova funcionalidade, para que possa atingir meu objetivo",
  role: "usuário",
  functionality: "uma nova funcionalidade", 
  benefit: "atingir meu objetivo",
  priority: "should_have",
  status: "backlog",
  type: "feature",
  storyPoints: 5,
  estimatedHours: 8,
  dependencies: [],
  acceptanceCriteria: [
    {
      id: generateCriteriaId(),
      description: "Dado que sou um usuário, quando usar esta funcionalidade, então devo conseguir atingir meu objetivo",
      completed: false
    }
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  tags: []
});

// Create a new acceptance criteria
export const createNewAcceptanceCriteria = (): AcceptanceCriteria => ({
  id: generateCriteriaId(),
  description: "",
  completed: false
});

// Add story to collection
export const addStoryToCollection = (stories: ProjectStories, newStory: UserStory): ProjectStories => ({
  ...stories,
  stories: [...stories.stories, newStory],
  totalStoryPoints: stories.totalStoryPoints + (newStory.storyPoints || 0),
  totalEstimatedHours: stories.totalEstimatedHours + (newStory.estimatedHours || 0),
  updatedAt: new Date().toISOString(),
});

// Remove story from collection
export const removeStoryFromCollection = (stories: ProjectStories, storyId: string): ProjectStories => {
  const storyToDelete = stories.stories.find(s => s.id === storyId);
  
  return {
    ...stories,
    stories: stories.stories.filter(s => s.id !== storyId),
    totalStoryPoints: stories.totalStoryPoints - (storyToDelete?.storyPoints || 0),
    totalEstimatedHours: stories.totalEstimatedHours - (storyToDelete?.estimatedHours || 0),
    updatedAt: new Date().toISOString(),
  };
};

// Update story in collection
export const updateStoryInCollection = (stories: ProjectStories, updatedStory: UserStory): ProjectStories => {
  const oldStory = stories.stories.find(s => s.id === updatedStory.id);
  const otherStories = stories.stories.filter(s => s.id !== updatedStory.id);

  // Recalculate totals
  const oldPoints = oldStory?.storyPoints || 0;
  const oldHours = oldStory?.estimatedHours || 0;
  const newPoints = updatedStory.storyPoints || 0;
  const newHours = updatedStory.estimatedHours || 0;

  return {
    ...stories,
    stories: [...otherStories, updatedStory],
    totalStoryPoints: stories.totalStoryPoints - oldPoints + newPoints,
    totalEstimatedHours: stories.totalEstimatedHours - oldHours + newHours,
    updatedAt: new Date().toISOString(),
  };
};

// Validate story data
export const validateStory = (story: UserStory): string[] => {
  const errors: string[] = [];

  if (!story.title?.trim()) {
    errors.push("Título é obrigatório");
  }

  if (!story.description?.trim()) {
    errors.push("Descrição é obrigatória");
  }

  if (!story.role?.trim()) {
    errors.push("Papel (role) é obrigatório");
  }

  if (!story.functionality?.trim()) {
    errors.push("Funcionalidade é obrigatória");
  }

  if (!story.benefit?.trim()) {
    errors.push("Benefício é obrigatório");
  }

  if (story.storyPoints && (story.storyPoints < 0 || story.storyPoints > 100)) {
    errors.push("Story points devem estar entre 0 e 100");
  }

  if (story.estimatedHours && (story.estimatedHours < 0 || story.estimatedHours > 1000)) {
    errors.push("Horas estimadas devem estar entre 0 e 1000");
  }

  return errors;
};

// Format story description for display
export const formatStoryDescription = (story: UserStory): string => {
  if (story.description) return story.description;
  
  return `Como ${story.role || 'usuário'}, eu quero ${story.functionality || 'uma funcionalidade'}, para que ${story.benefit || 'possa atingir meu objetivo'}`;
};

// Check if story is complete
export const isStoryComplete = (story: UserStory): boolean => {
  const hasBasicInfo = !!(story.title && story.description);
  const hasAcceptanceCriteria = story.acceptanceCriteria && story.acceptanceCriteria.length > 0;
  const hasEstimation = !!(story.storyPoints && story.estimatedHours);
  
  return hasBasicInfo && hasAcceptanceCriteria && hasEstimation;
};

// Sort stories by priority and then by creation date
export const sortStoriesByPriority = (stories: UserStory[]): UserStory[] => {
  const priorityOrder = {
    must_have: 1,
    should_have: 2,
    could_have: 3,
    wont_have: 4
  };

  return [...stories].sort((a, b) => {
    const aPriority = priorityOrder[a.priority] || 999;
    const bPriority = priorityOrder[b.priority] || 999;
    
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    // If same priority, sort by creation date (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};